import type { ReactElement } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useField } from 'formik'
import cc from 'classcat'

import { getPlain } from '../../../../../store/utils'
import { loadProductsByIds } from '../../../../../store/actions'
import { useInputDropdown } from '../../../../../utils/hooks/useInputDropdown'
import { useProductSuggestedSearch } from '../../../../../utils/hooks/useProductSuggestedSearch'
import FormGroup from '../../../../formComponents/FormikGroup'

type ProductPreviewProps = {
  config?: WorkspacePluginSettingsProps['config']
  productId?: string
  removeProduct: VoidFunction
} & TranslateProps

function ProductPreview({ config, productId, removeProduct, t }: Readonly<ProductPreviewProps>): ReactElement | null {
  const dispatch: ApiActionDispatch = useDispatch<GlobalDispatch>()
  const product = useSelector<State, Frontend.Product | undefined>((state) =>
    productId ? getPlain(state.getIn(['productData', 'productPlugin', productId])) : undefined,
  )
  const sellingCountryId = useSelector<State, number | null>(
    (state) => state.getIn(['shop', 'sellingCountryId']) || null,
  )

  useEffect(() => {
    if (productId && product?.productId !== productId) {
      dispatch(loadProductsByIds('productPlugin', [productId], sellingCountryId) as ResolvedApiAction<any>).then(
        (response: Core.PageableProductResult) => {
          // clear search input value on initial render in case there is no product,
          // e.g. when a previously added product was deleted in the cockpit/MBO
          if (response.totalNumberOfProducts === 0) removeProduct()
        },
      )
    }
    // removeProduct is excluded from the dependency array
    // as it causes infinite re-renders on opening the settings layer
  }, [productId, dispatch, product?.productId]) // eslint-disable-line react-hooks/exhaustive-deps

  const hashedThemeAssets = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'hashedThemeAssets']))
  const placeholderImageUrl = `/themes/epages.base/assets/images/${hashedThemeAssets.getIn([
    'epages.base/assets/images',
    'placeholder_180.jpg',
  ])}`
  const thumbnailUrl = config?.thumbnailUrl || ((url) => url)
  const productImageThumbnail = product?.image ? thumbnailUrl(product?.image.url) : placeholderImageUrl

  return (
    <div className="dali-plugin-productshowcase-products-list">
      <div className="dali-plugin-productshowcase-productslist-headline">{t('list.headline')}</div>
      {!product && <div className="dali-plugin-productshowcase-productslist-emptynote">{t('list.emptyNote')}</div>}
      {product && (
        <div className="dali-plugin-productshowcase-productslist-list">
          <div className={cc(['dali-plugin-productshowcase-wrapper', { invisible: !product?.isVisible }])}>
            <div
              className="dali-plugin-productshowcase-image"
              style={{ backgroundImage: `url('${productImageThumbnail}')` }}
            />
            <div className="dali-plugin-productshowcase-sku" title={product.sku}>
              {product.sku}
            </div>
            <div className="dali-plugin-productshowcase-name" title={product.name}>
              {product.name}
              {!product.isVisible && (
                <span>
                  <i className="dali-plugin-productshowcase-icon-invisible" title={t('invisibleIcon.label')} />
                </span>
              )}
            </div>
            <div className="dali-plugin-productshowcase-price">{product.price?.formatted}</div>
            <div
              className="dali-plugin-productshowcase-button-delete"
              title={t('deleteButton.label')}
              onClick={() => removeProduct()}
            />
          </div>
        </div>
      )}
    </div>
  )
}

type Props = {
  config?: WorkspacePluginSettingsProps['config']
} & TranslateProps

export function ProductSelection({ config, t }: Readonly<Props>): ReactElement {
  const hashedThemeAssets = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'hashedThemeAssets']))

  const [{ value }, , { setValue }] = useField<string>('productId')
  const [searchInput, setSearchInput] = useState<string | undefined>()
  const setSelectedProduct = useCallback(
    (productId: string) => {
      setValue(productId)
      setSearchInput(undefined)
    },
    [setValue],
  )

  const searchResults = useProductSuggestedSearch(value ? undefined : searchInput)
  const handleDropdownSelection = useCallback(
    (index: number) => {
      if (searchResults && index >= 0 && searchResults[index]) {
        setSelectedProduct(searchResults[index].productId)
      }
    },
    [searchResults, setSelectedProduct],
  )
  const [selectedIndex, wrapperRef, inputRef] = useInputDropdown(
    value ? [] : searchResults,
    setSearchInput,
    handleDropdownSelection,
  )

  return (
    <FormGroup name="productId" label={t('productIdSetting.label')}>
      <div className="ep-form-row">
        <div ref={wrapperRef} className="ep-form-smart ep-form-smart-with-suggestions">
          <div className="ep-form-smart-input">
            <input
              ref={inputRef}
              type="text"
              className="ep-form-smart-input-text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              aria-label={t('productIdSetting.label')}
              spellCheck={false}
              disabled={Boolean(value)}
              value={searchInput || ''}
              onClick={(event) => setSearchInput(event.currentTarget.value)}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
        </div>
        {searchResults?.length ? (
          <div className="ep-form-smart-suggestions">
            {searchResults.map(({ name, image, slug, productId }, index) => (
              <div
                key={slug}
                className={cc([
                  'ep-form-smart-suggestion',
                  selectedIndex === index && 'ep-form-smart-suggestion-active',
                ])}
                onClick={() => setSelectedProduct(productId)}
              >
                <div className="dali-plugin-productslider-wrapper">
                  <div
                    className="dali-plugin-productslider-image"
                    style={{
                      backgroundImage: image?.url
                        ? `url("${config?.thumbnailUrl?.(image.url)}")`
                        : `url('/themes/epages.base/assets/images/${hashedThemeAssets.getIn([
                            'epages.base/assets/images',
                            'placeholder_180.jpg',
                          ])}`,
                    }}
                  />
                  <div className="dali-plugin-productslider-name">{name}</div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <ProductPreview productId={value} t={t} removeProduct={() => setValue('')} config={config} />
    </FormGroup>
  )
}
